import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { searchItems } from '../services/api';
import type { SearchHit } from '../types';
import Icon from '../components/Icon';
import { Thumbnail } from './TypeGlyph';

function fmtBytes(bytes: number): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

/**
 * Results panel under the header search box. Anchored rather than routed, so
 * search works from any screen without hijacking navigation while typing.
 */
export default function SearchResults({ query, onPick, onClose }: {
  query: string;
  onPick: (hit: SearchHit) => void;
  onClose: () => void;
}) {
  // Debounce so a query fires per pause, not per keystroke.
  const [debounced, setDebounced] = useState(query);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(id);
  }, [query]);

  const { data, isFetching, isError, error } = useQuery({
    queryKey: ['search', debounced],
    queryFn: () => searchItems(debounced),
    enabled: debounced.length > 0,
    staleTime: 15_000,
  });

  if (!query.trim()) return null;

  const hits = data?.items ?? [];
  const total = data?.total ?? 0;
  // Show the previous result set while a new query is in flight rather than
  // flashing a spinner over it.
  const showEmpty = !isFetching && debounced.length > 0 && hits.length === 0;

  return (
    <div
      className="sk-search-results"
      style={{
        position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 60,
        background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 10,
        boxShadow: 'var(--shadow-2, 0 8px 32px rgba(0,0,0,0.14))',
        maxHeight: 420, overflowY: 'auto',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: '1px solid var(--line)', fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>
        <span style={{ flex: 1 }}>
          {isError
            ? 'Search failed'
            : isFetching && !data
              ? 'Searching…'
              : `${total} result${total === 1 ? '' : 's'}${total > hits.length ? ` · showing ${hits.length}` : ''}`}
        </span>
        {isFetching && data && <span className="t-spinner" style={{ width: 12, height: 12 }} />}
      </div>

      {isError && (
        <div style={{ padding: '14px 12px', fontSize: 'var(--fs-sm)', color: 'var(--danger)' }}>
          {(error as Error)?.message ?? 'Could not run the search.'}
        </div>
      )}

      {showEmpty && (
        <div style={{ padding: '20px 12px', textAlign: 'center', color: 'var(--ink-3)', fontSize: 'var(--fs-sm)' }}>
          <Icon name="search" size={22} color="var(--ink-4)" />
          <div style={{ marginTop: 8 }}>Nothing matches “{debounced}”</div>
        </div>
      )}

      {hits.map(hit => (
        <button
          key={hit.id}
          type="button"
          onClick={() => { onPick(hit); onClose(); }}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 10,
            padding: '9px 12px', background: 'transparent', border: 'none',
            borderBottom: '1px solid var(--line)', cursor: 'pointer', textAlign: 'left',
          }}
        >
          <Thumbnail item={hit} width={26} height={26} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {hit.name}
            </div>
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {hit.path.map(p => p.name).join(' / ')}
            </div>
          </div>
          <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', flexShrink: 0 }}>
            {fmtBytes(Number(hit.currentVersion?.size_bytes ?? hit.size_bytes ?? 0))}
          </span>
        </button>
      ))}
    </div>
  );
}
